import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { withOverlayControls } from '@eb/eds-structure';

import { constants as destinationConstants } from '@eb/destination-utils';
import * as analytics from '@eb/site-analytics';

import {
    getUserCurrentLocation as getUserCurrentLocationAction,
    searchLocationSuggestions as searchLocationSuggestionsAction,
} from '../actions/location';
import {
    handleApplyFilter as handleApplyFilterAction,
    handleRemoveFilter as handleRemoveFilterAction,
    searchAutocompleteSuggestions as searchAutocompleteSuggestionsAction,
    searchByBBoxOrPlace as searchByBBoxOrPlaceAction,
} from '../actions/search';
import { openCloseAnimatedDrawer } from '../actions/ui';

import {
    getSelectedCategory,
    getSelectedFormat,
    getSelectedDate,
    getSearchHeaderTitle,
} from '../selectors/search';

import SearchInput from '../components/searchHeader/SearchInput';
import AdminSearchSubHeader from '../components/AdminSearchSubHeader';
import MobileFilterHeader from '../components/searchHeader/MobileFilterHeader';
import { SUGGESTIONS_PROPTYPE, FOLLOWED_ORGS_FILTER_VALUE } from '../constants';

const { trackEventFromState } = analytics;
const { LOCATION_DIALOG_CONTENT } = destinationConstants;

import './SearchHeaderContainer.scss';

export class SearchHeader extends PureComponent {
    static propTypes = {
        /*
         * From withOverlayControls HoC
         */
        showOverlay: PropTypes.func.isRequired,
        hideOverlay: PropTypes.func,
        addOverlayNotification: PropTypes.func,
        hideOverlayNotification: PropTypes.func,
        trackGAEvent: PropTypes.func,
        handleApplyFilter: PropTypes.func,
        handleRemoveFilter: PropTypes.func,
        searchHeaderTitle: PropTypes.string,
        selectedCategory: PropTypes.string,
        selectedPrice: PropTypes.string,
        selectedDate: PropTypes.string,
        selectedFormat: PropTypes.string,
        isBotRequest: PropTypes.bool,
        currentPlace: PropTypes.string,
        term: PropTypes.string,
        locationSlug: PropTypes.string,
        locationDenied: PropTypes.bool,
        suggestions: SUGGESTIONS_PROPTYPE,
        handleUseCurrentLocation: PropTypes.func,
        handleSearchSuggestionChange: PropTypes.func,
        handleLocationSuggestionChange: PropTypes.func,
        handleLocationSelect: PropTypes.func,
        onGoogleMapsLoaded: PropTypes.func,
        isLoading: PropTypes.bool,
        facets: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.string,
                count: PropTypes.number,
                displayName: PropTypes.string,
            }),
        ),
        /**
         * Callback to execute when a filter is selected
         */
        openCloseAnimatedDrawer: PropTypes.func,
        /**
         * Type of individual filter active in the filter list
         */
        filterTypeToShow: PropTypes.string,
    };

    componentDidUpdate(prevProps) {
        const { locationDenied, showOverlay } = this.props;

        if (locationDenied !== prevProps.locationDenied) {
            showOverlay('dialog', LOCATION_DIALOG_CONTENT);
        }
    }

    _onUseCurrentLocation = () => {
        const {
            handleUseCurrentLocation,
            showOverlay,
            locationDenied,
        } = this.props;

        if (locationDenied) {
            return showOverlay('dialog', LOCATION_DIALOG_CONTENT);
        }

        return handleUseCurrentLocation();
    };

    render() {
        const {
            searchHeaderTitle,
            selectedCategory,
            selectedPrice,
            selectedDate,
            handleRemoveFilter,
            isBotRequest,
            locationSlug,
            currentPlace,
            term,
            handleSearchSuggestionChange,
            handleLocationSuggestionChange,
            handleApplyFilter,
            selectedFormat,
            suggestions,
            facets,
            isLoading,
            trackGAEvent,
            handleLocationSelect,
            onGoogleMapsLoaded,
            location,
            history,
            openCloseAnimatedDrawer,
            filterTypeToShow,
            selectedCurrency,
            selectedLanguage,
            followedOrganizers,
            selectedOrganizations,
            selectedHash,
        } = this.props;

        let headerContent = (
            <SearchInput
                hasHistory={true}
                handleSearchSuggestionChange={handleSearchSuggestionChange}
                handleLocationSuggestionChange={handleLocationSuggestionChange}
                handleUseCurrentLocation={this._onUseCurrentLocation}
                handleApplyFilter={handleApplyFilter}
                initialSearchQuery={term}
                currentPlace={currentPlace}
                suggestions={suggestions}
                trackGAEvent={trackGAEvent}
                handleLocationSelect={handleLocationSelect}
                onGoogleMapsLoaded={onGoogleMapsLoaded}
                location={location}
                history={history}
            />
        );

        // for bots we will just show the title
        if (isBotRequest) {
            headerContent = (
                <h1 className="eds-text-hm eds-text-color--grey-700 search-header__bot-friendly">
                    {searchHeaderTitle}
                </h1>
            );
        }

        return (
            <header className="search-header">
                {headerContent}
                <MobileFilterHeader
                    followedOrganizers={followedOrganizers}
                    selectedOrganizations={selectedOrganizations}
                    selectedHash={selectedHash}
                    selectedCurrency={selectedCurrency}
                    selectedLanguage={selectedLanguage}
                    selectedDate={selectedDate}
                    selectedCategory={selectedCategory}
                    selectedPrice={selectedPrice}
                    selectedFormat={selectedFormat}
                    handleRemoveFilter={handleRemoveFilter}
                    isBotRequest={isBotRequest}
                    locationSlug={locationSlug}
                    facets={facets}
                    isLoading={isLoading}
                    trackGAEvent={trackGAEvent.bind(this)}
                    history={history}
                    openCloseAnimatedDrawer={openCloseAnimatedDrawer}
                    filterTypeToShow={filterTypeToShow}
                />
                {history.location.hash === '#debug' && <AdminSearchSubHeader />}
            </header>
        );
    }
}

const _mapStateToProps = (state) => ({
    selectedPrice: state.search.eventSearch.price,
    selectedCategory: getSelectedCategory(state.search.eventSearch.tags),
    selectedFormat: getSelectedFormat(state.search.eventSearch.tags),
    selectedDate: getSelectedDate(
        state.search.eventSearch.dates,
        state.search.eventSearch.dateRange,
    ),
    selectedCurrency:
        state.search.eventSearch.currencies.length > 0
            ? state.search.eventSearch.currencies[0]
            : '',
    selectedLanguage:
        state.search.eventSearch.languages.length > 0
            ? state.search.eventSearch.languages
            : '',
    followedOrganizers:
        state.search.eventSearch.special.indexOf(FOLLOWED_ORGS_FILTER_VALUE) >
        -1,
    selectedOrganizations:
        state.search.eventSearch.organizationsOr.length > 0
            ? state.search.eventSearch.organizationsOr
            : false,
    selectedHash: state.search.eventSearch.hash,
    searchHeaderTitle: getSearchHeaderTitle(state),
    isBotRequest: state.app.isBotRequest,
    locationSlug: state.location.slug,
    currentPlace: state.location.currentPlace,
    term: state.search.eventSearch.q,
    locationDenied: state.location.locationDenied,
    currentLocationCoordinates: state.location.currentLocationCoordinates,
    suggestions: state.suggestions,
    isLoading: state.pagination.loading,
    trackGAEvent: (action) => trackEventFromState(state, action),
    location: state.location,
    filterTypeToShow: state.ui.filterTypeToShow,
});

const _mapDispatchToProps = (dispatch, ownProps) => ({
    handleApplyFilter: (...args) =>
        dispatch(handleApplyFilterAction(ownProps.history, ...args)),
    handleRemoveFilter: (...args) =>
        dispatch(handleRemoveFilterAction(ownProps.history, ...args)),

    handleUseCurrentLocation: (...args) =>
        dispatch(getUserCurrentLocationAction(ownProps.history, ...args)),

    handleLocationSuggestionChange: (...args) =>
        dispatch(searchLocationSuggestionsAction(ownProps.history, ...args)),
    handleSearchSuggestionChange: (...args) =>
        dispatch(searchAutocompleteSuggestionsAction(...args)),
    handleLocationSelect: (...args) =>
        dispatch(searchByBBoxOrPlaceAction(ownProps.history, ...args)),
    openCloseAnimatedDrawer: (...args) =>
        dispatch(openCloseAnimatedDrawer(...args)),
});

export default withRouter(
    withOverlayControls(
        connect(_mapStateToProps, _mapDispatchToProps)(SearchHeader),
    ),
);
